import { useEffect, useRef, useState } from 'react';

type Phase = 'idle' | 'pressed' | 'success' | 'done' | 'hidden';

export function ImOkayButton(props: { disabled?: boolean; onConfirm?: () => void }) {
  const [phase, setPhase] = useState<Phase>('idle');
  const runningRef = useRef(false);
  const timeoutsRef = useRef<number[]>([]);

  useEffect(() => {
    return () => {
      for (const t of timeoutsRef.current) window.clearTimeout(t);
      timeoutsRef.current = [];
    };
  }, []);

  const onClick = () => {
    if (props.disabled || runningRef.current) return;
    runningRef.current = true;

    setPhase('pressed');
    timeoutsRef.current.push(window.setTimeout(() => setPhase('success'), 300));
    timeoutsRef.current.push(window.setTimeout(() => setPhase('done'), 1700));
    timeoutsRef.current.push(window.setTimeout(() => setPhase('hidden'), 2200));

    props.onConfirm?.();
  };

  if (phase === 'hidden') return null;

  const isPressed = phase === 'pressed';
  const isSuccess = phase === 'success';
  const isDone = phase === 'done';
  const locked = phase !== 'idle';
  const showSuccess = isSuccess || isDone;

  return (
    <>
      <style>{`
        .iok-wrap{position:relative;display:flex;flex-direction:column;align-items:center;padding-bottom:0;transition: opacity .45s ease-out, padding-bottom .25s ease-out}
        .iok-wrap.isSuccess{padding-bottom:48px}
        .iok-wrap.isDone{opacity:0;pointer-events:none}

        .iok-btn-outer{position:relative;width:110px;height:64px;flex:0 0 auto}
        .iok-btn{position:absolute;inset:0;padding:0;width:100%;height:100%;border:1px solid rgba(200,173,106,0.18);outline:none;
          background: rgba(0,0,0,0.16);
          border-radius:22px;
          transition: transform 180ms ease, border-color 220ms ease, filter 220ms ease, background 220ms ease;
          cursor:pointer;color:#c8ad6a;overflow:hidden;z-index:2;
        }
        .iok-btn::before{
          content:'';position:absolute;inset:-50% -120%;
          background: linear-gradient(120deg, transparent 40%, rgba(200,173,106,0.08) 50%, transparent 60%);
          transform: translateX(-40%);opacity:1;transition: transform 900ms ease, opacity .25s ease-out;pointer-events:none;z-index:1;
        }
        .iok-btn:hover{transform:translateY(-1px);border-color:rgba(200,173,106,0.32);background:rgba(200,173,106,0.06)}
        .iok-btn:hover::before{transform:translateX(40%)}
        .iok-btn:focus-visible{border-color:rgba(210,180,91,0.55);box-shadow:0 0 0 3px rgba(210,180,91,0.12)}
        .iok-btn:active{transform:translateY(0)}
        .iok-btn:disabled{opacity:1;cursor:default}
        .iok-btn:disabled:hover{transform:none;filter:none;background:rgba(0,0,0,0.16);border-color:rgba(200,173,106,0.18)}

        .iok-content{position:relative;display:grid;padding:12px;width:100%;height:100%;grid-template-columns:1fr;grid-template-rows:1fr;
          border-radius:22px;z-index:2;
        }
        .iok-text{position:relative;margin:0;align-self:center;justify-self:center;font-size:15px;font-weight:800;letter-spacing:.2px;
          color:rgba(200,173,106,0.85);transition:opacity .20s ease-out, transform .20s ease-out;
        }
        .iok-btn-outer.isPressed .iok-text,.iok-btn-outer.isSuccess .iok-text{opacity:0;transform:scale(0.98)}

        .iok-check{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;opacity:0;pointer-events:none;z-index:3;transition:opacity .18s ease-out}
        .iok-btn-outer.isSuccess .iok-check{opacity:1}
        .iok-check svg{width:26px;height:26px}
        .iok-check path{fill:none;stroke:#c8ad6a;stroke-width:2.5;stroke-linecap:round;stroke-linejoin:round;stroke-dasharray:24;stroke-dashoffset:24}
        .iok-btn-outer.isSuccess .iok-check path{animation: iok-check-draw .36s ease-out forwards;animation-delay:.06s}
        @keyframes iok-check-draw{to{stroke-dashoffset:0}}

        .iok-msg{position:absolute;bottom:8px;left:50%;transform:translateX(-50%);
          width:200px;height:20px;line-height:20px;
          font-size:12px;font-style:italic;color:rgba(233,237,243,0.7);text-align:center;white-space:nowrap;
          opacity:0;pointer-events:none;transition: opacity .24s ease-out;
        }
        .iok-wrap.isSuccess .iok-msg,.iok-wrap.isDone .iok-msg{opacity:1}
      `}</style>

      <div className={`iok-wrap ${showSuccess ? 'isSuccess' : ''} ${isDone ? 'isDone' : ''}`.trim()}>
        <div className={`iok-btn-outer ${isPressed ? 'isPressed' : ''} ${showSuccess ? 'isSuccess' : ''}`.trim()}>
          <button className="iok-btn" onClick={onClick} disabled={!!props.disabled || locked} aria-label="I'm okay">
            <div className="iok-content">
              <p className="iok-text">I'm okay</p>
              <div className="iok-check" aria-hidden>
                <svg viewBox="0 0 24 24">
                  <path d="M5 12.5 L10 17.5 L19 8" />
                </svg>
              </div>
            </div>
          </button>
        </div>
        <div className="iok-msg">We're glad you're safe</div>
      </div>
    </>
  );
}
